import { Button, ButtonProps } from '../../design-system/Button';
import { ButtonGroup, ButtonGroupProps } from '../../design-system/ButtonGroup';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '../../design-system/DropdownMenu';
import { Checkbox } from '../../design-system/Checkbox';
import { Input } from '../../design-system/Input';
import {
  InputGroup,
  InputGroupInput,
  InputGroupProps,
  InputGroupSlot
} from '../../design-system/InputGroup';
import { cn } from '../../platform/dom/cn';
import { forwardRef, ReactNode, useMemo, useState } from 'react';
import { Plus, Search, X } from 'lucide-react';

export interface SearchSegmentsProps {
  children: ReactNode;
  className?: string;
  orientation?: 'column' | 'row';
  wrap?: boolean;
}

export function SearchSegments({
  children,
  className,
  orientation = 'column',
  wrap = true
}: SearchSegmentsProps) {
  const base =
    orientation === 'row'
      ? cn('flex gap-sm', wrap && 'flex-wrap')
      : 'flex flex-col gap-sm';
  return <div className={cn(base, className)}>{children}</div>;
}

export interface SearchSegmentsLayoutProps
  extends React.ComponentPropsWithRef<'div'> {
  children: ReactNode;
}

export const SearchSegmentsLayout = forwardRef<
  HTMLDivElement,
  SearchSegmentsLayoutProps
>(({ children, className, ...rest }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('flex flex-row flex-wrap gap-2', className)}
      {...rest}
    >
      {children}
    </div>
  );
});

SearchSegmentsLayout.displayName = 'SearchSegmentsLayout';

export interface SearchSegmentsInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: InputGroupProps['size'];
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

export const SearchSegmentsInput = forwardRef<
  HTMLInputElement,
  SearchSegmentsInputProps
>(
  (
    {
      value,
      onChange,
      placeholder = 'Search...',
      className,
      size = 'md',
      onKeyDown
    },
    ref
  ) => {
    return (
      <InputGroup className={cn(className)} size={size}>
        <InputGroupSlot>
          <Search className="size-4" />
        </InputGroupSlot>
        <InputGroupInput
          ref={ref}
          value={value}
          onChange={e => onChange?.(e.target.value)}
          placeholder={placeholder}
          onKeyDown={onKeyDown}
        />
      </InputGroup>
    );
  }
);

SearchSegmentsInput.displayName = 'SearchSegmentsInput';

export interface SearchSegmentProps extends Omit<ButtonGroupProps, 'children'> {
  fieldNameSlot: ReactNode;
  operatorSlot?: ReactNode;
  valueSlot?: ReactNode;
  onRemove?: () => void;
}

export function SearchSegment({
  className,
  orientation = 'horizontal',
  fieldNameSlot,
  operatorSlot,
  valueSlot,
  onRemove,
  ...rest
}: SearchSegmentProps) {
  return (
    <ButtonGroup orientation={orientation} className={cn(className)} {...rest}>
      {fieldNameSlot}
      {operatorSlot}
      {valueSlot}
      <Button
        variant="secondary"
        size="sm"
        className="px-2 text-slate-500"
        aria-label="Remove filter"
        onClick={onRemove}
      >
        <X className="size-4" />
      </Button>
    </ButtonGroup>
  );
}

export interface SearchSegmentsCreateButtonProps
  extends Omit<ButtonProps, 'onSelect'> {
  filters: ReadonlyArray<{
    id: string;
    label: string;
    description?: string;
    group?: string;
    icon?: ReactNode;
    options?: ReadonlyArray<{ id: string; label: string }>;
  }>;
  onApply: (filterId: string, values: string[]) => void;
  emptyState?: ReactNode;
}

export function SearchSegmentsCreateButton({
  filters,
  onApply,
  emptyState = 'No matching filters',
  children,
  ...buttonProps
}: SearchSegmentsCreateButtonProps) {
  const [open, setOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const [optionQueries, setOptionQueries] = useState<Record<string, string>>(
    {}
  );
  const [pendingSelections, setPendingSelections] = useState<
    Record<string, Set<string>>
  >({});

  const filteredFilters = useMemo(() => {
    const query = filterQuery.toLowerCase();
    return query
      ? filters.filter(filter => filter.label.toLowerCase().includes(query))
      : filters;
  }, [filters, filterQuery]);

  const toggleSelection = (
    filterId: string,
    optionId: string,
    checked: boolean
  ) => {
    setPendingSelections(prev => {
      const previous = prev[filterId] ?? new Set<string>();
      const next = new Set(previous);
      if (checked) {
        next.add(optionId);
      } else {
        next.delete(optionId);
      }
      return {
        ...prev,
        [filterId]: next
      };
    });
  };

  const handleApplyForFilter = (filterId: string) => {
    const selections = Array.from(pendingSelections[filterId] ?? new Set());
    if (!selections.length) {
      return;
    }
    onApply(filterId, selections);
    setPendingSelections(prev => ({
      ...prev,
      [filterId]: new Set()
    }));
    setOptionQueries(prev => ({
      ...prev,
      [filterId]: ''
    }));
    setOpen(false);
  };

  const handleImmediateApply = (filterId: string) => {
    onApply(filterId, []);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="md"
          {...buttonProps}
          className={cn(
            'gap-xs rounded-xl border-dashed border-border-muted px-lg py-sm text-base',
            buttonProps.className
          )}
        >
          <Plus className="size-4" />
          {children ?? 'Add filter'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[320px] space-y-2 p-2" align="start">
        <Input
          value={filterQuery}
          onChange={event => setFilterQuery(event.target.value)}
          placeholder="Search filters..."
          className="text-sm"
        />
        <div className="max-h-[360px] space-y-1 overflow-auto pr-1">
          {filteredFilters.length ? (
            filteredFilters.map(filter =>
              filter.options && filter.options.length ? (
                <DropdownMenuSub key={filter.id}>
                  <DropdownMenuSubTrigger className="flex items-center justify-between rounded-lg px-3 py-2 text-sm">
                    <span className="flex items-center gap-2">
                      {filter.icon}
                      {filter.label}
                    </span>
                    <span className="text-xs text-fg-muted">Browse</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    className="w-[280px] space-y-3 p-3"
                    sideOffset={8}
                    alignOffset={-4}
                  >
                    <div>
                      <p className="text-sm font-semibold text-fg-default">
                        {filter.label}
                      </p>
                      {filter.description && (
                        <p className="text-xs text-fg-muted">
                          {filter.description}
                        </p>
                      )}
                    </div>
                    <Input
                      value={optionQueries[filter.id] ?? ''}
                      onChange={event =>
                        setOptionQueries(prev => ({
                          ...prev,
                          [filter.id]: event.target.value
                        }))
                      }
                      placeholder={`Search ${filter.label.toLowerCase()}...`}
                      className="text-sm"
                    />
                    <div className="max-h-48 space-y-2 overflow-auto pr-1">
                      {(filter.options ?? [])
                        .filter(option =>
                          option.label
                            .toLowerCase()
                            .includes(
                              (optionQueries[filter.id] ?? '').toLowerCase()
                            )
                        )
                        .map(option => (
                          <label
                            key={option.id}
                            className="flex items-center gap-3 rounded-lg border border-border-muted px-3 py-2 text-sm text-fg-default"
                          >
                            <Checkbox
                              checked={
                                pendingSelections[filter.id]?.has(option.id) ??
                                false
                              }
                              onCheckedChange={checked =>
                                toggleSelection(
                                  filter.id,
                                  option.id,
                                  Boolean(checked)
                                )
                              }
                            />
                            {option.label}
                          </label>
                        ))}
                      {(filter.options ?? []).filter(option =>
                        option.label
                          .toLowerCase()
                          .includes(
                            (optionQueries[filter.id] ?? '').toLowerCase()
                          )
                      ).length === 0 && (
                        <p className="text-sm text-fg-muted">No results</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        className="flex-1"
                        disabled={
                          (pendingSelections[filter.id]?.size ?? 0) === 0
                        }
                        onClick={() => handleApplyForFilter(filter.id)}
                      >
                        Apply filter
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setPendingSelections(prev => ({
                            ...prev,
                            [filter.id]: new Set()
                          }))
                        }
                      >
                        Clear
                      </Button>
                    </div>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              ) : (
                <DropdownMenuItem
                  key={filter.id}
                  onSelect={() => handleImmediateApply(filter.id)}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm"
                >
                  {filter.icon}
                  <div className="flex flex-col text-left">
                    <span className="font-medium">{filter.label}</span>
                    {filter.description && (
                      <span className="text-xs text-fg-muted">
                        {filter.description}
                      </span>
                    )}
                  </div>
                </DropdownMenuItem>
              )
            )
          ) : (
            <div className="px-2 py-6 text-center text-sm text-fg-muted">
              {emptyState}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
